import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '../../lib/utils';
import { useResponsive } from '../../shared/hooks/useResponsive';
import { ThreadUnreadBadge } from '../../shared/components/common/ThreadUnreadBadge';

const Tabs = TabsPrimitive.Root;

/**
 * TabsList variants:
 * - "default": Compact pill-style tabs with muted background (for settings, dialogs)
 * - "underline": Full-width tabs with bottom border (for detail pages - more prominent)
 */
type TabsListVariant = 'default' | 'underline';

interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  variant?: TabsListVariant;
}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant = 'default', ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      variant === 'underline'
        ? 'inline-flex w-full items-center justify-start border-b bg-transparent h-auto p-0 text-muted-foreground overflow-x-auto scrollbar-hidden'
        // max-w-full caps the content-driven inline-flex width so overflow-x-auto can engage at 375px.
        // `touch:h-auto` releases the fixed 40px track on a coarse pointer so the 44px
        // floor on TabsTrigger has room — a floored trigger inside a `h-10` box either
        // clips or is squashed back under the floor. Half of one contract; the other half
        // is TABS_TRIGGER_TOUCH_FLOOR below, and both are pinned by
        // `__tests__/touchTargetFloor.test.tsx`.
        : 'inline-flex h-10 touch:h-auto max-w-full items-center justify-center rounded-md bg-muted p-1 text-muted-foreground overflow-x-auto scrollbar-hidden',
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

/**
 * Space kept clear beside the tab being scrolled into view. On a pointer device
 * the edge affordance is a 28px chevron sitting ON the strip, so a tab brought
 * flush to that edge would be "visible" underneath it; on touch there is no
 * chevron and this is only the hint that the strip continues.
 */
const ACTIVE_TAB_CHEVRON_GUTTER = 36;
const ACTIVE_TAB_TOUCH_GUTTER = 8;

/**
 * A `TabsList` that stays usable when its triggers are wider than the available
 * space — the standard for entity detail pages, whose related-entity tab strips
 * grow past the card at narrow widths.
 *
 * The list itself is the horizontal scroller (`overflow-x-auto`, native swipe on
 * touch, roving-tabindex arrow keys reach every tab and scroll it into view). On
 * pointer devices, where the scrollbar is hidden and there is no swipe, it adds
 * two affordances so the hidden tabs are reachable and discoverable:
 *  - Chevron buttons at the start/end edge, each shown only while there is more
 *    to scroll that way. Clicking pages the strip by ~75% of its width.
 *  - Shift + mouse-wheel scrolls horizontally (mirrors `KanbanScrollContainer`).
 *
 * It also keeps the ACTIVE trigger in view — on mount, when the trigger set
 * changes, on resize, and whenever a trigger's `data-state` flips. Without that,
 * a `?tab=` deep link to a late tab rendered the right panel under a strip still
 * at offset 0: the selected tab and its underline off-screen, so the user could
 * read the content but not see which tab they were on. Every detail strip
 * overflows at 375px and the wide ones overflow on desktop too, and the deep
 * links that land on a late tab are the common ones — `comment.mentioned` and
 * `comment.added` carry `deep_link_tab: "comments"`, which sits last or
 * near-last in most entities' `tabs` arrays.
 *
 * Drop-in for `TabsList`: same props, and the forwarded ref still points at the
 * underlying list. Do NOT wrap this in a `ScrollArea` or override its width — it
 * owns both.
 */
const ScrollableTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant = 'default', children, ...props }, forwardedRef) => {
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const { isTouchDevice } = useResponsive();
  const [edges, setEdges] = React.useState({ start: false, end: false });
  /** The trigger this strip has already been positioned for — see `scrollActiveIntoView`. */
  const positionedForRef = React.useRef<Element | null>(null);

  const measure = React.useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    // `scrollLeft` is negative in RTL on modern browsers; abs() gives the
    // distance travelled from the start edge in both directions.
    const scrolled = Math.abs(el.scrollLeft);
    const maxScroll = el.scrollWidth - el.clientWidth;
    const start = scrolled > 1;
    const end = scrolled < maxScroll - 1;
    setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);

  /**
   * Bring the active trigger inside the strip's own viewport.
   *
   * Deliberately NOT `scrollIntoView()`: that walks every ancestor scroller, so
   * a deep link would also scroll the PAGE down to the tab strip. Working on
   * `scrollLeft` keeps the side effect inside the one element that owns it.
   *
   * The arithmetic is on physical `getBoundingClientRect` edges, which makes it
   * direction-agnostic — `scrollBy({ left })` moves along the same physical axis
   * in both directions, so RTL needs none of the logical sign flip `page()`
   * below does (there, "toward the end" is a logical direction the caller named;
   * here both sides of the subtraction already come from the laid-out document).
   *
   * Always `behavior: 'auto'`, and that is a decision rather than an omission.
   * Radix activates a trigger on focus, so a click or an arrow-key step focuses
   * it and the BROWSER has already scrolled it flush into view by the time the
   * `data-state` flip reaches us — all this pass then owes is the gutter. A
   * smooth scroll on top of that native one is two motions per keypress, at two
   * different target offsets, and held arrow keys stack overlapping animations.
   * Instant also means there is no self-starting motion to gate on
   * `prefers-reduced-motion`, and no animation on a deep link's initial load.
   */
  const scrollActiveIntoView = React.useCallback((onlyIfSelectionChanged = false) => {
    const el = listRef.current;
    if (!el) return;
    const active = el.querySelector('[role="tab"][data-state="active"]');
    if (!active) return;

    // A PARENT RE-RENDER is not a reason to re-position. The effect below is keyed on
    // `children`, which is a fresh reference on every render of the page — so without
    // this the strip would jump back to the selection on any unrelated re-render (a
    // query refetch, an inline edit), fighting a user who had scrolled it elsewhere,
    // and would pay two forced layout reads each time. Resize and the `data-state`
    // observer pass nothing and always position: there the geometry or the selection
    // genuinely changed. Identity, not value — React reuses a trigger's DOM node.
    if (onlyIfSelectionChanged && positionedForRef.current === active) return;
    positionedForRef.current = active;

    // A strip that fits has nothing to scroll, and must gain no side effect.
    if (el.scrollWidth - el.clientWidth <= 1) return;

    // Keep the tab clear of the edge affordance so it is not brought "into view"
    // underneath a chevron button; on touch there is no button, only breathing room.
    const gutter = isTouchDevice ? ACTIVE_TAB_TOUCH_GUTTER : ACTIVE_TAB_CHEVRON_GUTTER;
    const list = el.getBoundingClientRect();
    const tab = active.getBoundingClientRect();
    const startShortfall = tab.left - gutter - list.left;
    const endShortfall = tab.right + gutter - list.right;
    // Start wins when a tab is wider than the gap between the two gutters, so the
    // label reads from its beginning rather than from its middle.
    const delta = startShortfall < 0 ? startShortfall : endShortfall > 0 ? endShortfall : 0;
    if (delta === 0) return;

    el.scrollBy({ left: delta, behavior: 'auto' });
    // Re-measure HERE rather than leaning on the `scroll` listener. This pass runs
    // from a layout effect, i.e. before that listener is attached, so at mount the
    // resulting scroll event reaches nobody and `edges` keeps the state it had at
    // offset 0 — leaving the end chevron drawn ON the tab just scrolled to (28px of
    // "Newsletters" under it, measured on the contact page at 375px).
    measure();
  }, [isTouchDevice, measure]);

  // Re-measure when the trigger set changes (tabs added/removed shift scrollWidth
  // and move the active tab — a detail strip gains its cross-module backlink tabs
  // a request after mount, which is exactly when a deep-linked one is off-screen).
  React.useLayoutEffect(() => {
    measure();
    scrollActiveIntoView(true);
  }, [measure, scrollActiveIntoView, children]);

  React.useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    // Scrolling re-measures the edges only: a user who scrolled away from the
    // selection must not be yanked back to it.
    const onScroll = () => measure();
    const onResize = () => {
      measure();
      scrollActiveIntoView();
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(onResize);
      ro.observe(el);
    }
    // A Radix `Tabs` with no controlled `value` re-renders its triggers from
    // context without re-rendering this list, so the layout effect above never
    // fires for a click — the `data-state` flip on the trigger is the only signal
    // that reaches here.
    const mo = new MutationObserver(() => scrollActiveIntoView());
    mo.observe(el, { attributes: true, attributeFilter: ['data-state'], subtree: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
      mo.disconnect();
    };
  }, [measure, scrollActiveIntoView]);

  // Non-passive wheel listener so Shift+Wheel can be redirected to horizontal
  // scroll (desktop mice without a horizontal wheel). React's synthetic wheel
  // events are passive and cannot preventDefault.
  React.useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.shiftKey) return;
      const delta = e.deltaY !== 0 ? e.deltaY : e.deltaX;
      if (delta === 0) return;
      e.preventDefault();
      el.scrollLeft += delta;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const page = React.useCallback((dir: 'start' | 'end') => {
    const el = listRef.current;
    if (!el) return;
    const rtl = typeof window !== 'undefined' && getComputedStyle(el).direction === 'rtl';
    const amount = Math.max(el.clientWidth * 0.75, 120);
    // Toward the end edge increases the visual offset; RTL flips the sign.
    const signed = (dir === 'end' ? amount : -amount) * (rtl ? -1 : 1);
    el.scrollBy({ left: signed, behavior: 'smooth' });
  }, []);

  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      listRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [forwardedRef],
  );

  // Native swipe covers touch; the chevrons are a pointer-only affordance and are
  // redundant with the tablist's arrow-key navigation, so they stay out of the
  // tab order and the a11y tree.
  const showButtons = !isTouchDevice;

  return (
    <div className="relative">
      <TabsList ref={setRefs} variant={variant} className={className} {...props}>
        {children}
      </TabsList>
      {showButtons && edges.start && (
        <TabScrollAffordance direction="start" onClick={() => page('start')} />
      )}
      {showButtons && edges.end && (
        <TabScrollAffordance direction="end" onClick={() => page('end')} />
      )}
    </div>
  );
});
ScrollableTabsList.displayName = 'ScrollableTabsList';

/**
 * One edge's scroll affordance: a `bg-card`→transparent fade scrim under a
 * chevron button. The scrim makes the tab clipped at that edge read as "there's
 * more" rather than sharply cut off, and — because the whole affordance is
 * `pointer-events-none` except the small button — clicks over the faded area
 * still fall through to the tab underneath; only the button itself scrolls.
 * Physical gradient directions are mirrored with the `rtl:` variant so the fade
 * stays anchored to the logical edge.
 */
function TabScrollAffordance({
  direction,
  onClick,
}: {
  direction: 'start' | 'end';
  onClick: () => void;
}) {
  const isStart = direction === 'start';
  const Icon = isStart ? ChevronLeft : ChevronRight;
  return (
    <div
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-y-0 z-10 flex items-center',
        isStart ? 'start-0 justify-start' : 'end-0 justify-end',
      )}
    >
      {/* Fade scrim — opaque at the edge, transparent toward the centre. */}
      <div
        className={cn(
          'absolute inset-y-0 w-12 from-card',
          isStart
            ? 'start-0 bg-gradient-to-r rtl:bg-gradient-to-l'
            : 'end-0 bg-gradient-to-l rtl:bg-gradient-to-r',
        )}
      />
      {/* `ScrollableTabsList` gates both chevrons on `!isTouchDevice`, because a touch
          device scrolls the strip by swiping. The affordance is also `aria-hidden` +
          `tabIndex={-1}`, so it is not a keyboard or screen-reader destination either —
          growing it would enlarge a decoration no finger can ever reach. */}
      {/* touch-target-ok: pointer-only — gated on !isTouchDevice, never rendered on touch */}
      <button
        type="button"
        tabIndex={-1}
        data-testid={`tab-scroll-${direction}`}
        onClick={onClick}
        className={cn(
          'pointer-events-auto relative flex h-7 w-7 items-center justify-center',
          'rounded-full border border-border bg-card text-muted-foreground shadow-sm',
          'transition-colors hover:bg-accent hover:text-foreground',
        )}
      >
        <Icon className="h-4 w-4 rtl:rotate-180" />
      </button>
    </div>
  );
}

/**
 * The 44px coarse-pointer tap-target floor shared by both trigger components.
 *
 * A tab is a label-sized box with no height of its own — the pill variant took its 32px
 * from `TabsList`'s `h-10` minus `p-1`, and the underline variant from `py-3` plus a 20px
 * line box. Neither is a declared height, so the static gate could not see either one and
 * the strip stayed a 32px finger target on every settings page, dialog and detail page in
 * the app. `min-h` and not `h-11` because a trigger with an icon or a `ThreadUnreadBadge`
 * must still be able to grow past 44px.
 */
const TABS_TRIGGER_TOUCH_FLOOR = 'touch:min-h-[44px]';

/**
 * TabsTrigger variants (should match TabsList variant):
 * - "default": Rounded with background on active
 * - "underline": Bottom border indicator on active
 */
type TabsTriggerVariant = 'default' | 'underline';

interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  variant?: TabsTriggerVariant;
  /** Optional icon to display before the tab label */
  icon?: React.ComponentType<{ className?: string }>;
}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant = 'default', icon: Icon, children, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      TABS_TRIGGER_TOUCH_FLOOR,
      variant === 'underline'
        ? 'inline-flex items-center justify-center whitespace-nowrap px-2.5 sm:px-3 py-3 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-b-2 border-transparent data-[state=active]:border-module-accent data-[state=active]:text-foreground flex-shrink-0'
        // Pill (default) active state: carry the module hue as a low-alpha
        // accent WASH (the same selection treatment as the nav selected item),
        // keeping the label as `text-foreground` ink so it stays AA-legible on
        // every module hue — the accent itself is only a 3:1 non-text mark, not a
        // 4.5:1 text color. `shadow-sm` keeps the pill's lift off the muted track.
        // Achromatic modules fall back to the slate accent default. Underline
        // variant above is unchanged.
        : 'inline-flex flex-shrink-0 items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-module-accent/15 dark:data-[state=active]:bg-module-accent/25 data-[state=active]:text-foreground data-[state=active]:shadow-sm',
      className
    )}
    {...props}
  >
    {Icon && <Icon className="h-4 w-4 me-1.5" />}
    {children}
  </TabsPrimitive.Trigger>
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

interface TabsTriggerWithIndicatorProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  /** Boolean dot indicator (legacy "has content" cue). */
  hasContent?: boolean;
  variant?: TabsTriggerVariant;
  /** Optional icon to display before the tab label */
  icon?: React.ComponentType<{ className?: string }>;
  /**
   * Total messages in the entity's thread. When defined, renders the read/unread
   * `ThreadUnreadBadge` inline (trailing) instead of the boolean `hasContent`
   * dot — nothing when `0`, a muted dot when all read, an accent count when
   * `unreadCount > 0`. Pair with `useThreadMarkRead` + `useThreadLiveRefresh`.
   */
  messageCount?: number;
  /** Unread messages for the current user (used with `messageCount`). */
  unreadCount?: number;
  /**
   * A domain count of work waiting on this tab — documents awaiting AI review,
   * pending approvals — rendered through `ThreadUnreadBadge`'s accent-count
   * state. Nothing when `0` or undefined. Prefer this over the
   * `messageCount`/`unreadCount` pair for anything that is not a message
   * thread, and pass `countAriaLabel`: the badge's default wording says
   * "unread messages", which would be a lie about what the number counts.
   */
  count?: number;
  /** Accessible label for `count` (required in practice — see above). */
  countAriaLabel?: string;
}

const TabsTriggerWithIndicator = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerWithIndicatorProps
>(({ className, hasContent, variant = 'default', icon: Icon, messageCount, unreadCount, count, countAriaLabel, children, ...props }, ref) => {
  const hasThreadCounts = messageCount !== undefined;
  const hasDomainCount = count !== undefined;
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'relative',
        TABS_TRIGGER_TOUCH_FLOOR,
        variant === 'underline'
          ? 'inline-flex items-center justify-center whitespace-nowrap px-2.5 sm:px-3 py-3 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-b-2 border-transparent data-[state=active]:border-module-accent data-[state=active]:text-foreground flex-shrink-0'
          // Pill active state carries the module hue as an accent wash with an
          // ink label — see TabsTrigger above.
          : 'inline-flex flex-shrink-0 items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-module-accent/15 dark:data-[state=active]:bg-module-accent/25 data-[state=active]:text-foreground data-[state=active]:shadow-sm',
        className
      )}
      {...props}
    >
      {Icon && <Icon className="h-4 w-4 me-1.5" />}
      {children}
      {hasDomainCount ? (
        // Both counts are the same number on purpose: every item of a work count
        // is outstanding, so the badge renders its accent-count state.
        <ThreadUnreadBadge
          messageCount={count}
          unreadCount={count}
          ariaLabel={countAriaLabel}
          className="ms-1.5"
        />
      ) : hasThreadCounts ? (
        <ThreadUnreadBadge
          messageCount={messageCount}
          unreadCount={unreadCount ?? 0}
          className="ms-1.5"
        />
      ) : (
        hasContent && (
          <span className="absolute -top-0.5 -end-0.5 h-2 w-2 rounded-full bg-primary" />
        )
      )}
    </TabsPrimitive.Trigger>
  );
});
TabsTriggerWithIndicator.displayName = 'TabsTriggerWithIndicator';

export { Tabs, TabsList, ScrollableTabsList, TabsTrigger, TabsContent, TabsTriggerWithIndicator };
